<script setup>
import { Link } from '@inertiajs/vue3';
import { computed, useAttrs } from 'vue';
import { extendTailwindMerge } from 'tailwind-merge';

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': ['text-micro', 'text-xs-plus'],
    },
  },
});

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const props = defineProps({
  outerClass: {
    type: String,
    required: false,
    default: '',
  },
  size: {
    type: String,
    required: false,
    default: 'medium',
  },
  theme: {
    type: String,
    required: false,
    default: 'primary',
  },
  iconPosition: {
    type: String,
    required: false,
    default: null,
  },
  text: {
    type: String,
    required: false,
    default: null,
  },
  type: {
    type: String,
    required: false,
    default: 'submit',
  },
  isLink: {
    type: Boolean,
    default: false,
  },
  to: {
    type: String,
    default: null,
  },
});

const classes = computed(() => {
  const base = [
    'btn-hover-lift flex text-center border items-center py-1.5 gap-2.5 focus:ring-2 rounded-lg justify-center font-medium whitespace-nowrap cursor-pointer transition-all',
    // Size
    props.size === 'small'  && 'text-xs px-3',
    props.size === 'medium' && 'text-sm px-4',
    props.size === 'large'  && 'text-base px-8',
  ].filter(Boolean).join(' ');

  const themes = {
    primary:   'border-transparent text-paper-light bg-theme-primary hover:bg-black dark:bg-primary-surface dark:text-theme-primary',
    secondary: 'border-transparent text-paper-light bg-theme-secondary hover:bg-theme-primary dark:bg-theme-secondary dark:text-paper-light dark:hover:bg-theme-primary',
    tertiary:  'border-transparent text-theme-secondary bg-grey-8 hover:bg-grey-600/30 dark:bg-primary-surface dark:text-theme-primary',
    warning:   'border-transparent text-paper-light bg-warning hover:bg-warning-dark ring-warning/20',
    outline:   'border-grey-6 text-theme-primary hover:bg-grey-600/30 dark:text-paper-light',
    ghost:     'border-transparent text-theme-primary hover:bg-grey-600/30 dark:text-paper-light',
    link:      'border-transparent text-theme-primary hover:underline',
  };

  const disabledClasses = attrs.disabled
    ? 'bg-divider !border-transparent text-disabled/80 pointer-events-none hover:bg-divider dark:bg-divider dark:text-disabled/80 dark:hover:bg-divider'
    : '';

  return twMerge(
    base,
    themes[props.theme] ?? '',
    disabledClasses,
    props.outerClass,
    attrs.class,
  );
});

const attrsWithoutClass = computed(() => {
  const { class: _, ...rest } = attrs;
  return rest;
});
</script>
<template>
  <button
    v-if="isLink === false"
    v-bind="attrsWithoutClass"
    :type="type"
    :class="classes"
  >
    <template v-if="props.iconPosition === 'left'">
      <slot name="leftIcon" />
    </template>
    <slot />
    {{ text }}
    <template v-if="props.iconPosition === 'right'">
      <slot name="rightIcon" />
    </template>
  </button>

  <Link
    v-else
    v-bind="attrsWithoutClass"
    :href="to"
    :class="classes"
  >
    <template v-if="props.iconPosition === 'left'">
      <slot name="leftIcon" />
    </template>
    <slot />
    {{ text }}
    <template v-if="props.iconPosition === 'right'">
      <slot name="rightIcon" />
    </template>
  </Link>
</template>
